import {
  Flex,
  type ListRef,
  Typography,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import Animated, {
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  interpolate,
  Extrapolation,
  withTiming,
} from 'react-native-reanimated';
import { ScrollContext } from '../context/ScrollContext';
import { useSetTab, useTabRef } from '../atom/tab.atom';
import { AnimatedList } from './AnimatedList';
import { SectionContainer } from './SectionContainer';
import { CoverImages } from './CoverImages';
import { MarketInfoBanner } from './MarketInfoBanner';
import { GoodsInfo } from './GoodsInfo';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ExpectedPurchasePrice } from './ExpectedPurchasePrice';
import { GoodsPromotions } from './GoodsPromotions';
import { CouponListBanner } from './CouponListBanner';
import { DeliveryInfo } from './DeliveryInfo';
import { MarketInfo } from './MarketInfo';
import type { GoodsDetailContentKeys } from '../types/GoodsDetailContentKeys';
import { useGoodsDetail } from '../queries/useGoodsDetail';
import { CategoryInfo } from './CategoryInfo';
import { ReviewSummary } from './ReviewSummary';
import { GoodsCarousel } from './GoodsCarousel';
import { ContentTab } from './ContentTab';
import type { GoodsDetailTab } from '../types/GoodsDetailTab';
import { GoodsContent } from './GoodsContent';
import { FloatingButtons } from './FloatingButtons';
import { BottomDockedButton } from './BottomDocked';
import { AffiliateFloatingButton } from './AffiliateFloating/AffiliateFloatingButton';
import { useAffiliateFloating } from '../hooks/useAffiliateFloating';
import { useLogViewGoodsDetail } from '../hooks/useLogViewGoodsDetail';
import { useLogClickGoodsDetailTab } from '../hooks/useLogClickGoodsDetailTab';
import { LiveCampaignBanner } from './LiveCampaignBanner';
import { useRelatedLiveCampaign } from '../queries/useRelatedLiveCampaign';
import { useExperimentGroup } from 'shared/src/context/UserContext/useExperimentGroup';
import { useLogGoodsAnalyticsEvent } from '../hooks/useLogGoodsAnalyticsEvent';
import { useGoodsBasic } from '../queries/useGoodsBasic';
import { Metadata } from 'shared/src/component';
import {
  getMetaDescription,
  getMetaKeywords,
  getMetaTitle,
  getProductMeta,
} from '../utils/goodsMetaData';
import { isAndroid, isIOS, isNotNil, isWeb } from 'shared/src/utils';
import { usePreloadQuery } from '../hooks/usePreloadQuery';
import type { ListRenderItem } from 'react-native';
import { useGoodsSno } from '../hooks/useGoodsSno';

const BOTTOM_DOCKED_BUTTON_HEIGHT = 56;
const BOTTOM_DOCKED_BUTTON_PADDING = 12 * 2;
const LIVE_CAMPAIGN_BANNER_HEIGHT = 48;

const useLiveCampaignCGroupLogging = () => {
  const { data: campaign } = useRelatedLiveCampaign();
  const isCGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['C'],
  });
  const logEvent = useLogGoodsAnalyticsEvent();
  const hasLogged = useRef(false);

  useEffect(() => {
    if (!campaign || !isCGroup || hasLogged.current) return;
    hasLogged.current = true;

    logEvent({
      name: 'VIEW_SHOPLIVE_TARGET_GOODS_DETAIL',
      channel: ['braze'],
      analytics: campaign.logging.analytics,
    });
  }, [campaign, isCGroup, logEvent]);
};

export const GoodsDetailContents: React.FC = () => {
  const scrollY = useSharedValue(0);
  const goodsSno = useGoodsSno();

  const tabScrollX = useSharedValue(0);
  const { width } = useScreenSize();
  const { bottom } = useSafeArea();
  const listRef = useRef<ListRef<GoodsDetailContentKeys> | null>(null);
  const tabLastScrollYValueRef = useRef<{
    [key in GoodsDetailTab]: number;
  }>({
    INFORMATION: 0,
    REVIEW: 0,
    SIZE: 0,
    INQUIRY: 0,
  });

  const sectionHeightRef = useRef<{ [key: string]: number }>({});

  const recalcStickyPosition = useCallback(() => {
    const heights = sectionHeightRef.current;
    const nextY = Object.keys(heights)
      .filter(key => key !== 'content' && key !== 'content-tab')
      .reduce((prev, cur) => prev + heights[cur], 0);
    if (stickyItemPositionYRef.current !== nextY) {
      stickyItemPositionYRef.current = nextY;
    }
  }, []);

  const handleChangeHeight = useCallback(
    (key: string, height: number) => {
      if (sectionHeightRef.current[key] === height) return;
      sectionHeightRef.current[key] = height;
      recalcStickyPosition();
    },
    [recalcStickyPosition],
  );

  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();
  const { data: liveCampaign } = useRelatedLiveCampaign();
  const isBGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['B'],
  });
  const showLiveBanner = !!liveCampaign && isBGroup;

  const tabRef = useTabRef();
  const setTab = useSetTab();
  const logClickGoodsDetailTab = useLogClickGoodsDetailTab();

  useLiveCampaignCGroupLogging();
  usePreloadQuery();

  const scrollHandler = useAnimatedScrollHandler(event => {
    scrollY.value = event.contentOffset.y;
  });

  const stickyItemPositionYRef = useRef(0);

  const componentList = useMemo<GoodsDetailContentKeys[]>(() => {
    const list: GoodsDetailContentKeys[] = [];
    list.push('cover');
    list.push('promotions');
    list.push('market');
    list.push('info');
    list.push('expected-purchase-price');
    list.push('coupons');
    list.push('delivery-info');
    // PDP 저성과 컴포넌트 제거에 따른 컴포넌트 제거
    // by 연준호 2026. 01. 19
    // list.push('market-info');
    if (goodsDetail?.component_visibility.review_summary) {
      list.push('review-summary');
    }

    // PDP 저성과 컴포넌트 제거에 따른 컴포넌트 제거
    // by 연준호 2026. 01. 12
    // if (goodsDetail?.component_visibility.category) {
    //   list.push('category-info');
    // }
    if (isAndroid || (isNotNil(goodsBasic) && isNotNil(goodsDetail))) {
      list.push('carousel');
    }
    list.push('content-tab');

    if (isAndroid || (isNotNil(goodsBasic) && isNotNil(goodsDetail))) {
      list.push('content');
    }

    return list;
  }, [goodsBasic, goodsDetail]);

  const stickyHeaderIndices = useMemo(
    () => [componentList.findIndex(key => key === 'content-tab')],
    [componentList],
  );

  const marginBottom = useMemo(() => {
    const DOCKED_PRIMARY_BUTTON_HEIGHT =
      BOTTOM_DOCKED_BUTTON_HEIGHT + BOTTOM_DOCKED_BUTTON_PADDING;
    return bottom + DOCKED_PRIMARY_BUTTON_HEIGHT;
  }, [bottom]);

  const scrollToStickyView = useCallback(() => {
    listRef.current?.scrollToOffset({
      offset: stickyItemPositionYRef.current,
      animated: true,
    });
  }, []);

  const scrollToOffset = useCallback((offset: number, animated: boolean) => {
    listRef.current?.scrollToOffset({
      offset,
      animated,
    });
  }, []);

  const changeContentTab = useCallback(
    (nextTab: GoodsDetailTab) => {
      setTab(nextTab);
      const tabList: GoodsDetailTab[] = [
        'INFORMATION',
        'REVIEW',
        'SIZE',
        'INQUIRY',
      ];
      if (isIOS) {
        tabScrollX.value = withTiming(
          tabList.findIndex(t => t === nextTab),
          { duration: 200 },
        );
      }
    },
    [setTab, tabScrollX],
  );

  const onChangeTab = useCallback(
    (nextTab: GoodsDetailTab) => {
      const prevTab = tabRef.current;
      if (prevTab !== nextTab) {
        logClickGoodsDetailTab(nextTab);
        const currentScrollY = scrollY.value;
        //마지막 스크롤 저장
        tabLastScrollYValueRef.current = {
          ...tabLastScrollYValueRef.current,
          [prevTab]: currentScrollY,
        };

        if (currentScrollY > stickyItemPositionYRef.current) {
          setTimeout(() => {
            scrollToOffset(
              Math.max(
                tabLastScrollYValueRef.current[nextTab],
                stickyItemPositionYRef.current,
              ),
              false,
            );
          }, 1);
        }
      }
      setTab(nextTab);
    },
    [tabRef, setTab, logClickGoodsDetailTab, scrollY, scrollToOffset],
  );

  useLogViewGoodsDetail();

  const { floatingText, showFloating, onViewFloating, onClickFloating } =
    useAffiliateFloating();

  const listStyle = useMemo(() => ({ flex: 1, marginBottom }), [marginBottom]);

  const listContentContainerStyle = useMemo(
    () =>
      showLiveBanner ? { paddingTop: LIVE_CAMPAIGN_BANNER_HEIGHT } : undefined,
    [showLiveBanner],
  );

  const renderItem = useCallback<ListRenderItem<GoodsDetailContentKeys>>(
    ({ item }) => {
      if (item === 'cover') {
        return (
          <SectionContainer
            height={width}
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CoverImages />
          </SectionContainer>
        );
      }

      if (item === 'promotions') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsPromotions />
          </SectionContainer>
        );
      }

      if (item === 'market') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MarketInfoBanner />
          </SectionContainer>
        );
      }

      if (item === 'info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsInfo />
          </SectionContainer>
        );
      }

      if (item === 'expected-purchase-price') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ExpectedPurchasePrice />
          </SectionContainer>
        );
      }

      if (item === 'coupons') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CouponListBanner />
          </SectionContainer>
        );
      }

      if (item === 'delivery-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <DeliveryInfo />
          </SectionContainer>
        );
      }

      if (item === 'market-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MarketInfo />
          </SectionContainer>
        );
      }
      if (item === 'review-summary') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ReviewSummary />
          </SectionContainer>
        );
      }

      if (item === 'category-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CategoryInfo />
          </SectionContainer>
        );
      }

      if (item === 'carousel') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsCarousel />
          </SectionContainer>
        );
      }

      if (item === 'content-tab') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ContentTab />
          </SectionContainer>
        );
      }

      if (item === 'content') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsContent />
          </SectionContainer>
        );
      }

      return (
        <Flex height={300} backgroundColor={'white'}>
          <Typography>{item}</Typography>
        </Flex>
      );
    },
    [width, handleChangeHeight],
  );

  const bannerAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [0, LIVE_CAMPAIGN_BANNER_HEIGHT],
            [0, -LIVE_CAMPAIGN_BANNER_HEIGHT],
            Extrapolation.CLAMP,
          ),
        },
      ],
    };
  });

  const scrollContextValue = useMemo(
    () => ({
      listRef,
      scrollY,
      tabScrollX,
      scrollToStickyView,
      changeContentTab,
      onChangeTab,
    }),
    [
      scrollToStickyView,
      changeContentTab,
      onChangeTab,
      listRef,
      scrollY,
      tabScrollX,
    ],
  );

  return (
    <ScrollContext value={scrollContextValue}>
      {isNotNil(goodsBasic) && (
        <Metadata
          title={getMetaTitle(goodsBasic)}
          imgSrc={goodsBasic?.cover_images[0] ?? ''}
          path={`/goods/${goodsBasic.sno}`}
          description={getMetaDescription(goodsBasic)}
          keywords={getMetaKeywords(goodsBasic)}
          productMeta={getProductMeta(goodsBasic)}
          isCanonical
        />
      )}
      {showLiveBanner && (
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            },
            bannerAnimatedStyle,
          ]}
        >
          <LiveCampaignBanner />
        </Animated.View>
      )}
      <AnimatedList
        ref={listRef}
        {...(isWeb ? { nativeID: 'goods-detail-scroll' } : {})}
        style={listStyle}
        contentContainerStyle={listContentContainerStyle}
        onScroll={scrollHandler}
        data={componentList}
        stickyHeaderIndices={stickyHeaderIndices}
        renderItem={renderItem}
      />
      <Flex
        position={'absolute'}
        left={0}
        right={0}
        bottom={0}
        alignItems={'center'}
      >
        <Flex width={width}>
          {/* web에서 해당 key값을 할당 하지 않으면 unmount가 되지 않고 단순히 re-render가됨.
          이전 상품에서 선택했던 옵션을 그대로 보여줍니다.
          key값을 추가해서 새로 받아 올 수 있도록 합니다.
          */}
          <BottomDockedButton key={goodsSno.toString()} />
        </Flex>
      </Flex>
      <FloatingButtons
        bottomOffset={marginBottom}
        scrollToYPosition={isAndroid ? stickyItemPositionYRef.current : 0}
      />
      {showFloating && (
        <AffiliateFloatingButton
          text={floatingText}
          marginBottom={marginBottom}
          onPress={onClickFloating}
          onView={onViewFloating}
        />
      )}
    </ScrollContext>
  );
};
